import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Drawer from '../../ui/Drawer'
import Tabs from '../../ui/Tabs'
import Loader from '../../shared/Loader'
import Button from '../../ui/Button'
import DownloadIcon from '../../ui/icons/DownloadIcon'
import { showToast } from '../../shared/Toast'
import { formatDateTime } from '../../../utils/dateHelpers'
import useIssueDetail from '../hooks/useIssueDetail'
import useAssignableUsers from '../hooks/useAssignableUsers'
import IssuesService from '../../../services/api/IssuesService'
import StatusDot from '../IssueStatuses/StatusDot'
import IssueManagementTab from './IssueManagementTab'
import IssueResultsTab from './IssueResultsTab'
import groupUsersByRole from '../shared/groupUsersByRole'
import { TOAST_CONTAINER_ID } from './constants'

const FULL_NAME_KEY = 'full_name'
const USER_IDS_KEY = 'user_ids'
const REMOVED_PHOTO_IDS_KEY = 'removed_photo_ids'
const ISSUE_STATUS_ID_KEY = 'issue_status_id'

const ERROR_MSG = 'Une erreur est survenue lors de la modification de l’alerte.'
const SUCCESS_MSG = 'La modification de l’alerte a bien été effectuée.'

const TABS = [
  { value: 'management', label: 'Gestion de l’alerte' },
  { value: 'results', label: 'Détail des résultats' },
  { value: 'additional', label: 'Contrôle supplémentaire' }
]

const TITLE_CLASS = 'tw:flex-1 tw:flex tw:items-center tw:gap-2 tw:text-xl! '
  + 'tw:font-medium! tw:text-gray-900! tw:m-0! tw:min-w-0'
const EXPORT_LINK_CLASS = 'tw:inline-flex tw:items-center tw:justify-center tw:size-9 '
  + 'tw:rounded-md! tw:border tw:border-solid tw:border-gray-300 tw:bg-white '
  + 'tw:text-gray-700 tw:no-underline! tw:hover:bg-gray-100 tw:flex-shrink-0'
const META_CLASS = 'tw:px-6 tw:pt-4 tw:text-sm tw:text-gray-600 tw:m-0!'
const TABS_WRAP_CLASS = 'tw:px-6 tw:pt-4'
const PLACEHOLDER_CLASS = 'tw:text-sm tw:text-gray-500 tw:py-8 tw:text-center'
const NO_ADDITIONAL_MSG = 'Pas de contrôle supplémentaire réalisé.'
const FOOTER_CLASS = 'tw:flex! tw:items-center tw:justify-end tw:gap-2 tw:p-4 '
  + 'tw:border-t tw:border-solid tw:border-gray-300'

// Keep every attributed user, enriching from the option (role label, etc.) when it
// Exists. Filtering by options would silently drop attributees missing from the
// Assignable list (staff hidden from a non-staff viewer, user removed from collect).
const buildSelectedUsers = (attribution, options) => {
  const optionByValue = new Map(options.map(option => [option.value, option]))
  return attribution.map(user => optionByValue.get(user.value) || user)
}

const EditIssueDrawer = ({ collectId, issueStatuses, issueId = null, isOpen, onClose, onUpdated = null }) => {
  const { issue, loading, error } = useIssueDetail(collectId, issueId, { enabled: isOpen })
  const userOptions = useAssignableUsers(collectId, isOpen, TOAST_CONTAINER_ID)
  const recipientOptions = useMemo(() => groupUsersByRole(userOptions), [userOptions])
  const [activeTab, setActiveTab] = useState('management')
  const [statusValue, setStatusValue] = useState(null)
  const selectedStatus = issueStatuses.find(status => status.id === statusValue) || null
  const [commentValue, setCommentValue] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [newPhotos, setNewPhotos] = useState([])
  const [removedPhotoIds, setRemovedPhotoIds] = useState([])
  const [saving, setSaving] = useState(false)
  // Prevent a late userOptions fetch from clobbering an in-progress edit.
  const usersTouched = useRef(false)

  useEffect(() => {
    setActiveTab('management')
  }, [issueId])

  useEffect(() => {
    usersTouched.current = false
    setNewPhotos([])
    setRemovedPhotoIds([])
    if (!issue) {
      setStatusValue(null)
      setCommentValue('')
      setSelectedUsers([])
      return
    }
    setStatusValue(issue.status_id)
    setCommentValue(issue.comment || '')
  }, [issue])

  useEffect(() => {
    if (!issue || usersTouched.current)
      return
    const attribution = (issue.attribution || []).map(user => ({
      id: user.id, value: user.id, label: user[FULL_NAME_KEY]
    }))
    setSelectedUsers(buildSelectedUsers(attribution, userOptions))
  }, [issue, userOptions])

  const handleStatusChange = useCallback(statusId => setStatusValue(Number(statusId)), [])
  const handleCommentChange = useCallback(event => setCommentValue(event.target.value), [])
  const handleUsersChange = useCallback(value => {
    usersTouched.current = true
    setSelectedUsers(value || [])
  }, [])

  const handleAddPhotos = useCallback(files => {
    setNewPhotos(previous => [...previous, ...files])
  }, [])
  const handleRemoveExisting = useCallback(signedId => {
    setRemovedPhotoIds(previous => [...previous, signedId])
  }, [])
  const handleRemoveNew = useCallback(index => {
    setNewPhotos(previous => previous.filter((unused, position) => position !== index))
  }, [])

  const handleClose = useCallback(() => {
    if (!saving)
      onClose()
  }, [saving, onClose])

  const handleSave = useCallback(async() => {
    if (!issueId || saving)
      return
    setSaving(true)
    try {
      await IssuesService.updateIssue(collectId, issueId, {
        [ISSUE_STATUS_ID_KEY]: statusValue,
        comment: commentValue,
        [USER_IDS_KEY]: selectedUsers.map(option => option.value),
        photos: newPhotos,
        [REMOVED_PHOTO_IDS_KEY]: removedPhotoIds
      })
      showToast(SUCCESS_MSG, 'success', TOAST_CONTAINER_ID)
      if (onUpdated)
        onUpdated()
      onClose()
    } catch (caught) {
      showToast(caught?.message || ERROR_MSG, 'error', TOAST_CONTAINER_ID)
    } finally {
      setSaving(false)
    }
  }, [
    collectId,
    issueId,
    saving,
    statusValue,
    commentValue,
    selectedUsers,
    newPhotos,
    removedPhotoIds,
    onUpdated,
    onClose
  ])

  const renderAdditionalTab = () => {
    if (!issue.answer?.has_additional)
      return <p className={PLACEHOLDER_CLASS}>{NO_ADDITIONAL_MSG}</p>
    return <IssueResultsTab results={issue.additional_results} />
  }

  const renderTabContent = () => {
    if (activeTab === 'results')
      return <IssueResultsTab results={issue.results} />
    if (activeTab === 'additional')
      return renderAdditionalTab()
    return (
      <IssueManagementTab
        collectName={issue.collect?.name}
        commentValue={commentValue}
        issue={issue}
        issueStatuses={issueStatuses}
        newPhotos={newPhotos}
        recipientOptions={recipientOptions}
        removedPhotoIds={removedPhotoIds}
        selectedUsers={selectedUsers}
        statusValue={statusValue}
        onAddPhotos={handleAddPhotos}
        onCommentChange={handleCommentChange}
        onRemoveExisting={handleRemoveExisting}
        onRemoveNew={handleRemoveNew}
        onStatusChange={handleStatusChange}
        onUsersChange={handleUsersChange}
      />
    )
  }

  const renderHeader = () => (
    <Drawer.Header onClose={handleClose}>
      <h2 className={TITLE_CLASS}>
        <span>{`Alerte #${issue.id}`}</span>
        {selectedStatus ? <StatusDot color={selectedStatus.color} label={selectedStatus.name} /> : null}
      </h2>
      <a
        aria-label="Télécharger le PDF de l’alerte"
        className={EXPORT_LINK_CLASS}
        href={issue.pdf_url}
        rel="noreferrer"
        target="_blank"
      >
        <DownloadIcon />
      </a>
    </Drawer.Header>
  )

  const renderContent = () => {
    // Drawer stays mounted for the slide animation; render nothing when closed.
    if (!issueId)
      return null
    if (loading && !issue)
      return <Drawer.Body><div className="tw:flex tw:justify-center"><Loader /></div></Drawer.Body>
    if (error || !issue)
      return <Drawer.Body><p className={PLACEHOLDER_CLASS}>{ERROR_MSG}</p></Drawer.Body>
    return (
      <>
        {renderHeader()}
        <p className={META_CLASS}>{`Dernière édition : ${formatDateTime(issue.updated_at)}`}</p>
        <div className={TABS_WRAP_CLASS}>
          <Tabs
            items={TABS.filter(tab => tab.value !== 'additional' || issue.collect?.has_additional_answer)}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <Drawer.Body>{renderTabContent()}</Drawer.Body>
        <footer className={FOOTER_CLASS}>
          <Button disabled={saving} variant="neutral" onClick={handleClose}>Annuler</Button>
          <Button disabled={saving} variant="primary" onClick={handleSave}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </footer>
      </>
    )
  }

  return (
    <Drawer
      ariaLabel="Gestion de l’alerte"
      closeOnBackdrop={!saving}
      closeOnEscape={!saving}
      isOpen={isOpen}
      onClose={handleClose}
    >
      {renderContent()}
    </Drawer>
  )
}

EditIssueDrawer.propTypes = {
  collectId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  issueId: PropTypes.number,
  issueStatuses: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func
}

EditIssueDrawer.defaultProps = {
  issueId: null,
  issueStatuses: [],
  onUpdated: null
}

export default EditIssueDrawer
